'use client';

import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { trackThemeToggle } from '@/lib/analytics';

type Theme = 'dark' | 'light';

interface ThemeContextValue {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const STORAGE_KEY = 'theme';
const THEME_COLOR = { dark: '#0F0F13', light: '#FFFFFF' };

// Inline, unminified on purpose — this string is injected verbatim into a
// <script> tag in app/[locale]/layout.tsx and runs before hydration, so it
// must be plain ES5-safe JS with no imports. It reads the stored preference
// (falling back to 'light', the default) and applies the `dark` class
// to <html> synchronously, before first paint, so there's no flash of the
// wrong theme.
export const themeInitScript = `(function(){try{var t=localStorage.getItem('${STORAGE_KEY}');if(t==='dark'){document.documentElement.classList.add('dark');var m=document.querySelector('meta[name="theme-color"]');if(m){m.setAttribute('content','${THEME_COLOR.dark}');}}}catch(e){}})();`;

function applyTheme(theme: Theme) {
  document.documentElement.classList.toggle('dark', theme === 'dark');
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', THEME_COLOR[theme]);
}

export default function ThemeProvider({ children }: { children: React.ReactNode }) {
  // Matches the inline init script's default so hydration doesn't mismatch;
  // the real value is read from the DOM class right after mount.
  const [theme, setThemeState] = useState<Theme>('light');

  useEffect(() => {
    const initial: Theme = document.documentElement.classList.contains('dark') ? 'dark' : 'light';
    setThemeState(initial);
    // The <head> meta[name="theme-color"] starts hardcoded to the brand
    // purple (#6c63ff) in app/[locale]/layout.tsx, which matches neither
    // --bg light (#FFFFFF) nor --bg dark (#0F0F13). themeInitScript already
    // corrects it pre-paint for returning dark-mode users; this covers every
    // other case (first-ever visit, light mode, storage read failure) so the
    // browser chrome color always lines up with the active theme, not just
    // after the user manually toggles it.
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', THEME_COLOR[initial]);
  }, []);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // localStorage unavailable (private browsing, etc.) — theme still
      // applies for this session, it just won't persist.
    }
    applyTheme(next);
  }, []);

  const toggleTheme = useCallback(() => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    // Only the explicit toggle button counts as a tracked action — setTheme
    // itself isn't instrumented here, since it also runs on initial mount
    // sync (not a user action) for any future caller that uses it directly.
    trackThemeToggle(next);
  }, [theme, setTheme]);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
}
